import { ErrorMessage, PageContainer, Spinner } from "@/components";
import {
  useApplicationsQuery,
  useWithdrawApplicationMutation,
} from "@/hooks/api/application.queries";
import { ApplicationType } from "@/services/application.service";
import { useInternshipByIdQuery } from "@/hooks/api/internship.queries";
import {
  Briefcase,
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  Inbox,
  Loader2,
  RefreshCw,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// The full application status enum, enforced by a DB CHECK constraint on the
// backend. All eleven are mapped so a chip never renders a raw snake_case value.
const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bg: string; border: string; dot: string }
> = {
  applied: {
    label: "Applied",
    color: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-100",
    dot: "bg-amber-500",
  },
  received: {
    label: "Received",
    color: "text-sky-700",
    bg: "bg-sky-50",
    border: "border-sky-100",
    dot: "bg-sky-500",
  },
  under_review: {
    label: "Under Review",
    color: "text-blue-700",
    bg: "bg-blue-50",
    border: "border-blue-100",
    dot: "bg-blue-500",
  },
  shortlisted: {
    label: "Shortlisted",
    color: "text-violet-700",
    bg: "bg-violet-50",
    border: "border-violet-100",
    dot: "bg-violet-500",
  },
  interview_scheduled: {
    label: "Interview Scheduled",
    color: "text-indigo-700",
    bg: "bg-indigo-50",
    border: "border-indigo-100",
    dot: "bg-indigo-500",
  },
  interviewed: {
    label: "Interviewed",
    color: "text-cyan-700",
    bg: "bg-cyan-50",
    border: "border-cyan-100",
    dot: "bg-cyan-500",
  },
  offer_extended: {
    label: "Offer Extended",
    color: "text-teal-700",
    bg: "bg-teal-50",
    border: "border-teal-100",
    dot: "bg-teal-500",
  },
  offer_accepted: {
    label: "Offer Accepted",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
    dot: "bg-emerald-500",
  },
  offer_declined: {
    label: "Offer Declined",
    color: "text-slate-600",
    bg: "bg-slate-100",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
  rejected: {
    label: "Rejected",
    color: "text-red-700",
    bg: "bg-red-50",
    border: "border-red-100",
    dot: "bg-red-400",
  },
  withdrawn: {
    label: "Withdrawn",
    color: "text-gray-600",
    bg: "bg-gray-100",
    border: "border-gray-200",
    dot: "bg-gray-400",
  },
};

const humanize = (status: string) =>
  status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const getStatusConfig = (status: string) =>
  STATUS_CONFIG[status.toLowerCase()] || {
    label: humanize(status),
    color: "text-gray-600",
    bg: "bg-gray-100",
    border: "border-gray-200",
    dot: "bg-gray-400",
  };

// Statuses the backend accepts a withdrawal from — anything from an offer
// onwards is answered with accept/decline instead, not a withdrawal.
const WITHDRAWABLE_STATUSES = [
  "applied",
  "received",
  "under_review",
  "shortlisted",
  "interview_scheduled",
  "interviewed",
];

const IN_PROGRESS_STATUSES = ["applied", "received", "under_review"];

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "under_review", label: "Under Review" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "interview_scheduled", label: "Interview Scheduled" },
  { value: "offer_extended", label: "Offer Extended" },
  { value: "rejected", label: "Rejected" },
];

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const [withdrawTarget, setWithdrawTarget] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useApplicationsQuery({
    status: statusFilter || undefined,
    page: 1,
    limit: 50,
  });

  const withdrawMutation = useWithdrawApplicationMutation();

  const applications: ApplicationType[] = data?.items || [];
  const total = data?.total || 0;

  if (isError) {
    return (
      <ErrorMessage message="Failed to load applications." onRetry={() => refetch()} />
    );
  }

  return (
    <PageContainer className="min-h-screen">
      {/* Hero Header */}
      <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-700 to-purple-700 p-8 text-white shadow-xl">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-yellow-300" />
            <span className="text-sm font-semibold text-indigo-200 uppercase tracking-widest">
              My Activity
            </span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-2">My Applications</h1>
          <p className="text-indigo-200 text-base max-w-xl">
            Track the status of all your internship applications in one place.
          </p>
          <div className="mt-5 flex items-center gap-3 text-sm text-indigo-200">
            <span className="flex items-center gap-1.5">
              <FileText size={14} /> {isLoading ? "..." : total} Application{total !== 1 ? "s" : ""}
            </span>
            <span className="w-1 h-1 bg-indigo-400 rounded-full" />
            <button
              onClick={() => refetch()}
              className="flex items-center gap-1.5 hover:text-white transition"
            >
              <RefreshCw size={13} className={cn("inline", isLoading && "animate-spin")} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <div className="flex items-center gap-1.5 mr-2 text-gray-500">
          <Filter size={14} />
          <span className="text-xs font-semibold uppercase tracking-wider">Filter:</span>
        </div>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${statusFilter === f.value
                ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"
              }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Spinner />
          <p className="text-gray-400 text-sm animate-pulse">Loading your applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 bg-white rounded-2xl border border-dashed border-gray-200 text-center">
          <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-5">
            <Inbox size={32} className="text-indigo-300" />
          </div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No applications yet</h3>
          <p className="text-gray-400 text-sm max-w-xs mb-6">
            {statusFilter
              ? `No applications with status "${getStatusConfig(statusFilter).label}".`
              : "You haven't applied for any internships. Start exploring now!"}
          </p>
          <button
            onClick={() => navigate("/discover/internships")}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <Briefcase size={16} /> Browse Internships
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {applications.map((app) => {
            const appStatus = app.status.toLowerCase();
            const status = getStatusConfig(app.status);
            const canWithdraw = WITHDRAWABLE_STATUSES.includes(appStatus);
            return (
              <div
                key={app.id}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all duration-300 overflow-hidden"
              >
                {/* Top accent bar per status */}
                <div
                  className={`h-1 w-full ${appStatus === "offer_accepted" || appStatus === "offer_extended"
                      ? "bg-gradient-to-r from-emerald-400 to-teal-500"
                      : appStatus === "rejected" || appStatus === "offer_declined"
                        ? "bg-gradient-to-r from-red-400 to-rose-500"
                        : appStatus === "withdrawn"
                          ? "bg-gradient-to-r from-gray-300 to-gray-400"
                          : appStatus === "shortlisted"
                            ? "bg-gradient-to-r from-violet-400 to-purple-500"
                            : "bg-gradient-to-r from-indigo-400 to-blue-500"
                    }`}
                />

                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
                        <Briefcase size={18} className="text-indigo-500" />
                      </div>
                      <InternshipTitleDisplay id={app.internship_id} />
                    </div>
                    {/* Status area */}
                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${status.color} ${status.bg} ${status.border} shrink-0`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot} ${IN_PROGRESS_STATUSES.includes(appStatus) ? "animate-pulse" : ""}`} />
                        {status.label}
                      </span>
                    </div>
                  </div>

                  {/* Timeline info */}
                  <div className="space-y-2 text-sm text-gray-500 mb-5">
                    <div className="flex items-center gap-2">
                      <Clock size={13} className="text-gray-400 shrink-0" />
                      <span>Applied: {new Date(app.applied_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <RefreshCw size={13} className="text-gray-400 shrink-0" />
                      <span>Updated: {new Date(app.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                    </div>
                    {app.cover_letter && (
                      <div className="flex items-start gap-2">
                        <FileText size={13} className="text-gray-400 shrink-0 mt-0.5" />
                        <span className="line-clamp-2 text-xs text-gray-400 italic">"{app.cover_letter}"</span>
                      </div>
                    )}
                  </div>

                  {/* Status-specific message */}
                  {appStatus === "offer_extended" && (
                    <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-teal-50 rounded-xl border border-teal-100 text-xs text-teal-700 font-medium">
                      <Sparkles size={14} /> You have an offer waiting. Head to Offers to respond.
                    </div>
                  )}
                  {appStatus === "offer_accepted" && (
                    <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-xl border border-emerald-100 text-xs text-emerald-700 font-medium">
                      <CheckCircle2 size={14} /> Congratulations! You accepted this offer.
                    </div>
                  )}
                  {appStatus === "rejected" && (
                    <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-red-50 rounded-xl border border-red-100 text-xs text-red-600 font-medium">
                      <XCircle size={14} /> This application was not selected. Keep applying!
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => navigate(`/discover/internships/${app.internship_id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition"
                    >
                      View Role <ChevronRight size={14} />
                    </button>
                    {(appStatus === "interview_scheduled" || appStatus === "interviewed") && (
                      <button
                        onClick={() => navigate("/interviews")}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-600 text-sm font-semibold hover:bg-indigo-100 transition"
                      >
                        <CalendarCheck size={14} /> Interviews
                      </button>
                    )}
                    {canWithdraw && (
                      <button
                        onClick={() => setWithdrawTarget(app.id)}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-red-100 bg-red-50 text-red-600 text-sm font-semibold hover:bg-red-100 transition"
                      >
                        <XCircle size={14} /> Withdraw
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Withdraw Confirm Dialog */}
      <AlertDialog open={!!withdrawTarget} onOpenChange={() => setWithdrawTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold">Withdraw Application?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Your application will be marked as withdrawn and the recruiter will
              stop considering it. Any interview scheduled for it will not go
              ahead. You can apply again while the position is still open.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (withdrawTarget) {
                  await withdrawMutation.mutateAsync(withdrawTarget);
                  setWithdrawTarget(null);
                }
              }}
              className="bg-red-500 hover:bg-red-600 text-white rounded-xl"
              disabled={withdrawMutation.isPending}
            >
              {withdrawMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Withdrawing...
                </span>
              ) : (
                "Yes, Withdraw"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}

function InternshipTitleDisplay({ id }: { id: string }) {
  const { data, isLoading, isError } = useInternshipByIdQuery(id);

  if (isLoading) {
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium animate-pulse w-20 h-4 bg-gray-100 rounded"></p>
        <p className="animate-pulse w-32 h-5 bg-gray-100 rounded"></p>
      </div>
    );
  }

  // null = the recruiter removed this posting (fetch resolved, nothing there).
  if (data === null) {
    return (
      <div>
        <p className="text-[10px] text-gray-400 font-bold mb-0.5 uppercase tracking-wider">
          Position removed
        </p>
        <p className="text-sm font-bold text-slate-500 leading-tight line-clamp-1">
          This position is no longer available
        </p>
        <p className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {id.slice(0, 8)}</p>
      </div>
    );
  }

  // Transient failure — don't claim the posting is gone.
  if (isError) {
    return (
      <div>
        <p className="text-[10px] text-gray-400 font-bold mb-0.5 uppercase tracking-wider">
          Details unavailable
        </p>
        <p className="text-sm font-bold text-slate-500 leading-tight line-clamp-1">
          Could not load role details
        </p>
        <p className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {id.slice(0, 8)}</p>
      </div>
    );
  }

  const title = data?.title || data?.position || "Untitled Position";
  const company = data?.company_name || data?.company || "Confidential";

  return (
    <div>
      <p className="text-[10px] text-gray-500 font-bold mb-0.5 uppercase tracking-wider">{company}</p>
      <p className="text-sm font-bold text-slate-800 leading-tight line-clamp-1 group-hover:text-indigo-700 transition-colors">
        {title}
      </p>
      <p className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {id.slice(0, 8)}</p>
    </div>
  );
}
